/*----------------------------------
- OPEN GRAPH BANNERS (SSOT)
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { EDITION_PAGES, resolveEditionPersonaFromPath } from '@uniquedomains/components/contracts/editions/public';
import { SALES_PATH } from '@uniquedomains/components/contracts/sales/public';
import { AUCTIONS_PATH } from '@uniquedomains/components/contracts/auctions/public';
import { REGISTRARS_PATH } from '@uniquedomains/components/contracts/registrars/public';
import { AVAILABILITY_PATH, WHOIS_PATH } from '@uniquedomains/components/contracts/lookup/limits';
import { AGENDA_PATH, TLD_HUB_PATH } from '@uniquedomains/core/domains/tlds';

/*----------------------------------
- TYPES
----------------------------------*/

export type WebsiteOpenGraphBanner = {
    alt: string;
    filename: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

export const WEBSITE_OPEN_GRAPH_PUBLIC_DIRECTORY = '/public/og';

export const DEFAULT_WEBSITE_OPEN_GRAPH_BANNER: WebsiteOpenGraphBanner = {
    alt: 'Unique Domains one-word domain decision platform',
    filename: 'master-1200x630.png',
};

/**
 * Shared by the Domaining News archive and the newsletter page. Both point at the
 * same daily brief, and the published-banner set dedupes by object identity, so
 * reusing this object keeps one artwork instead of two identical files.
 */
export const DOMAINING_NEWS_OPEN_GRAPH_BANNER: WebsiteOpenGraphBanner = {
    alt: 'Daily Domaining News from Unique Domains',
    filename: 'og-domaining-news.png',
};

/**
 * Both daily editions pages (ADR-0074) share one object, like the market pages share
 * `DOMAINS_OPEN_GRAPH_BANNER`: the published list dedupes by identity, so two literals naming one
 * file would publish it twice.
 */
const EDITIONS_OPEN_GRAPH_BANNER: WebsiteOpenGraphBanner = {
    alt: 'Trending one-word domains and business ideas, a new edition every day',
    filename: 'og-opportunities.png',
};

/**
 * Shared by the `/domains` hub and the TLD pages (`/tld`, `/tld/<slug>`, `/tld/applications`):
 * the TLD pages replaced the `/domains/tld/*` listings, which wore this banner through the
 * `/domains/` prefix fallback. Reused by identity so the published set keeps one artwork.
 */
export const DOMAINS_OPEN_GRAPH_BANNER: WebsiteOpenGraphBanner = {
    alt: 'Explore one-word domains with Unique Domains',
    filename: 'og-domains-hub.png',
};

export const WEBSITE_OPEN_GRAPH_ROUTE_BANNERS: Readonly<Record<string, WebsiteOpenGraphBanner>> = {
    '/': DEFAULT_WEBSITE_OPEN_GRAPH_BANNER,
    /*
     * No agenda artwork is supplied, and the two nearest banners would misdescribe the page (the
     * News one promises a weekly digest, the Browse one a map of names), so the agenda wears the
     * master banner on purpose. Point this at its own file once one is supplied.
     */
    [AGENDA_PATH]: DEFAULT_WEBSITE_OPEN_GRAPH_BANNER,
    '/api': { alt: 'Unique Domains API documentation', filename: 'og-api.png' },
    '/compare': { alt: 'Compare Unique Domains with other domain tools', filename: 'og-compare-hub.png' },
    '/compare/atom': { alt: 'Unique Domains compared with Atom.com', filename: 'og-vs-atom.png' },
    '/compare/bishopi': { alt: 'Unique Domains compared with Bishopi', filename: 'og-vs-bishopi.png' },
    '/compare/brandbucket': {
        alt: 'Unique Domains compared with BrandBucket',
        filename: 'og-vs-brandbucket.png',
    },
    '/compare/dotdb': { alt: 'Unique Domains compared with dotDB', filename: 'og-vs-dotdb.png' },
    '/compare/estibot': { alt: 'Unique Domains compared with Estibot', filename: 'og-vs-estibot.png' },
    '/compare/expireddomains-net': {
        alt: 'Unique Domains compared with ExpiredDomains.net',
        filename: 'og-vs-expireddomains-net.png',
    },
    '/compare/instant-domain-search': {
        alt: 'Unique Domains compared with Instant Domain Search',
        filename: 'og-vs-instant-domain-search.png',
    },
    '/compare/mononames': { alt: 'Unique Domains compared with mononames', filename: 'og-vs-mononames.png' },
    '/compare/namebio': { alt: 'Unique Domains compared with NameBio', filename: 'og-vs-namebio.png' },
    '/compare/one-word-domains': {
        alt: 'Unique Domains compared with oneword.domains',
        filename: 'og-vs-one-word-domains.png',
    },
    '/domains': DOMAINS_OPEN_GRAPH_BANNER,
    [TLD_HUB_PATH]: DOMAINS_OPEN_GRAPH_BANNER,
    // The sales page shares the market banner of the TLD pages until it has its own (ADR-0067).
    [SALES_PATH]: DOMAINS_OPEN_GRAPH_BANNER,
    // The auctions page shares it too until it has its own (ADR-0076).
    [AUCTIONS_PATH]: DOMAINS_OPEN_GRAPH_BANNER,
    // The registrar hub too (ADR-0079); its profiles wear it through the prefix fallback below.
    [REGISTRARS_PATH]: DOMAINS_OPEN_GRAPH_BANNER,
    /*
     * The lookup tools (ADR-0078) wear the master banner until they have their own artwork: the
     * market banner promises a map of names, and a lookup answers one. Their result pages wear
     * the same banner through the prefix fallback below, never an image naming the looked-up name.
     */
    [WHOIS_PATH]: DEFAULT_WEBSITE_OPEN_GRAPH_BANNER,
    [AVAILABILITY_PATH]: DEFAULT_WEBSITE_OPEN_GRAPH_BANNER,
    '/newsletter': DOMAINING_NEWS_OPEN_GRAPH_BANNER,
    '/pricing': { alt: 'Unique Domains plans and pricing', filename: 'og-pricing.png' },
    '/resources/domaining-news': DOMAINING_NEWS_OPEN_GRAPH_BANNER,
    '/product/appraise': { alt: 'Appraise domains with every source at once', filename: 'og-appraise.png' },
    // The daily editions (ADR-0074) keep the trends banner until a dated edition's own card
    // (`/<page>/<date>/share.png`) replaces it on that edition's page.
    [EDITION_PAGES.investor.path]: EDITIONS_OPEN_GRAPH_BANNER,
    [EDITION_PAGES.founder.path]: EDITIONS_OPEN_GRAPH_BANNER,
    '/product/radars': { alt: 'Track fresh domain matches with Radars', filename: 'og-radars.png' },
    '/product/search': { alt: 'Search one-word domains with Unique Domains', filename: 'og-search.png' },
    '/product/verify': { alt: 'Verify a domain before buying it', filename: 'og-verify.png' },
    '/product/watchlists': { alt: 'Track domain changes with Watchlists', filename: 'og-watchlists.png' },
    '/solutions/founders': { alt: 'Unique Domains for founders', filename: 'og-founders.png' },
    '/solutions/investors': { alt: 'Unique Domains for domain investors', filename: 'og-investors.png' },
    '/solutions/studios': { alt: 'Unique Domains for studios and agencies', filename: 'og-studios.png' },
    '/team': { alt: 'About Unique Domains', filename: 'og-team.png' },
};

export const WEBSITE_OPEN_GRAPH_RESERVED_BANNERS: readonly WebsiteOpenGraphBanner[] = [
    { alt: 'Unique Domains investor relations', filename: 'og-investor-relations.png' },
];

export const WEBSITE_OPEN_GRAPH_PUBLISHED_BANNERS: readonly WebsiteOpenGraphBanner[] = [
    ...new Set([...Object.values(WEBSITE_OPEN_GRAPH_ROUTE_BANNERS), ...WEBSITE_OPEN_GRAPH_RESERVED_BANNERS]),
];

/*----------------------------------
- HELPERS
----------------------------------*/

const normalizePathname = (requestUrl: string): string => {
    const pathname = new URL(requestUrl, 'https://unique.domains').pathname.replace(/\/+$/, '');
    return pathname || '/';
};

export const websiteOpenGraphBannerPublicPath = (banner: WebsiteOpenGraphBanner): string => {
    return `${WEBSITE_OPEN_GRAPH_PUBLIC_DIRECTORY}/${banner.filename}`;
};

export const resolveWebsiteOpenGraphBanner = (requestUrl: string): WebsiteOpenGraphBanner => {
    const pathname = normalizePathname(requestUrl);
    const exactBanner = WEBSITE_OPEN_GRAPH_ROUTE_BANNERS[pathname];
    if (exactBanner) return exactBanner;

    if (pathname.startsWith('/api/')) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS['/api'];
    if (pathname.startsWith('/compare/')) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS['/compare'];
    if (pathname.startsWith('/domains/')) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS['/domains'];
    if (pathname.startsWith(`${WHOIS_PATH}/`)) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS[WHOIS_PATH];
    if (pathname.startsWith(`${AVAILABILITY_PATH}/`)) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS[AVAILABILITY_PATH];
    // The insight pages and the applications list wear the hub's banner.
    if (pathname.startsWith(`${TLD_HUB_PATH}/`)) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS[TLD_HUB_PATH];
    if (pathname.startsWith(`${REGISTRARS_PATH}/`)) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS[REGISTRARS_PATH];
    // Every page of the editions (a trend, a month) wears its page's banner.
    const editionPersona = resolveEditionPersonaFromPath(pathname);
    if (editionPersona) return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS[EDITION_PAGES[editionPersona].path];
    if (pathname.startsWith('/resources/domaining-news/')) {
        return WEBSITE_OPEN_GRAPH_ROUTE_BANNERS['/resources/domaining-news'];
    }

    return DEFAULT_WEBSITE_OPEN_GRAPH_BANNER;
};
